/*
 * Copyright (c) 2024, King Living
 * Dynamic Page Routing per TSD Contentful: Contentful by slug → SFCC category fallback → NotFound (KL-770).
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';
import {useQuery, useQueryClient} from '@tanstack/react-query';

import loadable from '@loadable/component';

import {useAccessToken, useCategory, useStores} from '@salesforce/commerce-sdk-react';
import {Center, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {Page as ContentfulPage} from '@amblique/king-living/app/components/contentful/page';
import PdpPageSkeleton from '@amblique/king-living/app/components/pdp-page-skeleton';
import Seo from '@amblique/king-living/app/components/seo';
import {useProduct} from '@amblique/king-living/app/hooks/use-product';
import {useBodyPageType} from '@amblique/king-living/app/hooks/use-body-page-type';
import {
  buildCategorySeoQueryKey,
  buildPageBySlugQueryKey,
  buildProductSeoQueryKey,
  usePageBySlug,
} from '@amblique/king-living/app/contentful/hooks';
import {
  ADMIN_STORES_STALE_TIME_MS,
  adminStoreByIdQueryKey,
  fetchAdminStoreById,
} from '@amblique/king-living/app/hooks/use-multi-site-stores';
import {resolveStoreSiteId} from '@amblique/king-living/app/utils/showroom-countries';
import {getSlugFromPathname} from '@amblique/king-living/app/utils/slug-from-path';
import {
  buildPageSeoBundle,
  findBlogIdInMainRegion,
  isBlogArticlePage,
  isFaqCmsPage,
  PRODUCT_SEO_EXPAND,
  SEO_MANAGED_META_NAMES,
} from '@amblique/king-living/app/utils/seo-utils';

// Paths that match catch-all but are not app pages (Chrome, PWA Kit internals, etc.)
const SYSTEM_PATH_PATTERN = /^\/(\.well-known|__mrt|favicon\.ico)(\/|$)/;

/** Contentful-style page fallback — centered in main, not top-left KitKat spinner. */
const contentfulPageFallback = (
  <Center minH="75vh" width="100%">
    <Skeleton borderRadius="full" boxSize="12" />
  </Center>
);

const PageNotFound = loadable(() => import('@salesforce/retail-react-app/app/pages/page-not-found'));
const ProductList = loadable(() => import('@amblique/king-living/app/pages/product-list'), {
  fallback: contentfulPageFallback,
});

function DynamicPageSeo({children, seoBundle}) {
  if (!seoBundle) return null;

  const {jsonLdGraph, ...seoProps} = seoBundle;

  return (
    <Seo {...seoProps} jsonLdGraph={jsonLdGraph ?? null}>
      {children}
    </Seo>
  );
}

DynamicPageSeo.propTypes = {
  children: PropTypes.node,
  seoBundle: PropTypes.object,
};

/**
 * Catch-all handler: Contentful page by slug → SFCC category fallback (path → id) → NotFound.
 * Validates page.categoryId / productId / storeId in SFCC before rendering (KL-770).
 */
function DynamicPage() {
  const location = useLocation();
  const pathname = location.pathname;
  const appOrigin = useAppOrigin();
  const {res} = useServerContext();
  const {locale, site} = useMultiSite();
  const {getTokenWhenReady} = useAccessToken();
  const currency = locale?.preferredCurrency || 'AUD';

  const isSystemPath = useMemo(
    () => typeof pathname === 'string' && SYSTEM_PATH_PATTERN.test(pathname),
    [pathname]
  );

  const slug = useMemo(() => getSlugFromPathname(pathname), [pathname]);
  const slugPrimary = slug || 'home';

  // Shell (routing/SEO fields) then slim (mainRegion). PDP gates slim until shell resolves productId.
  const {
    data: pageShell,
    isLoading: pageShellLoading,
    isFetched: pageShellFetched,
  } = usePageBySlug(slugPrimary, {
    variant: 'shell',
    queryOptions: {enabled: !isSystemPath},
  });

  const {
    data: pageSlim,
    isFetched: pageSlimFetched,
  } = usePageBySlug(slugPrimary, {
    queryOptions: {
      enabled: !isSystemPath && (!pageShell?.productId || pageShellFetched),
    },
  });

  const page = pageSlim ?? pageShell;
  const pageFetched = pageShellFetched && (pageShell != null || pageSlimFetched);

  const contentfulService = useService(ContentfulService);
  const queryClient = useQueryClient();

  const pageQueryKey = useMemo(() => {
    if (!contentfulService) return null;

    return buildPageBySlugQueryKey({contentfulService, site, slug: slugPrimary, variant: 'shell'});
  }, [contentfulService, site, slugPrimary]);

  const cachedPage = pageQueryKey ? queryClient.getQueryData(pageQueryKey) : undefined;

  // SFCC category IDs use hyphens (e.g. bedroom-beds); path segments are joined with -
  const categoryIdFromPath = useMemo(
    () => (slug ? slug.replace(/\//g, '-') : ''),
    [slug]
  );
  // Skip category fallback for /page/* (blog, content) – not SFCC categories, avoids bogus API 404
  const isContentPath = slug?.startsWith('page/') ?? false;
  // Legacy `/product/:id` URLs resolve only via Contentful slug (e.g. product/sku); never SFCC PLP fallback
  const isProductPath = slug?.startsWith('product/') ?? false;
  const tryCategoryFallback =
    pageFetched && !pageShell && !pageSlim && !!categoryIdFromPath && !isContentPath && !isProductPath;

  const {data: categoryFromPath, isFetched: categoryFallbackFetched} = useCategory(
    {parameters: {id: categoryIdFromPath}},
    {enabled: tryCategoryFallback}
  );

  const categoryId = page?.categoryId ?? cachedPage?.categoryId;
  const productId = pageShell?.productId ?? pageSlim?.productId ?? cachedPage?.productId;
  // Route gate: leave loader once slim query settles with a page (mainRegion may be empty — CMS not always populated).
  // After ContentfulPage mounts, Container + ProductDetail keep PdpPageSkeleton via PageContext / isFetching.
  const mainLoadingFallback =
    (productId || isProductPath) ? <PdpPageSkeleton /> : contentfulPageFallback;
  const hasSlimMainBody = pageSlimFetched && pageSlim != null;
  const storeId = page?.storeId ?? cachedPage?.storeId;
  const cmsStoreSiteId = page?.storeSiteId ?? cachedPage?.storeSiteId;
  const storeSiteId = resolveStoreSiteId(cmsStoreSiteId, site?.id);
  const isCrossSiteStore = Boolean(storeId && storeSiteId && storeSiteId !== site?.id);

  const categoryQueryKey = useMemo(() => {
    if (!categoryId) return null;

    return buildCategorySeoQueryKey({categoryId, locale, site});
  }, [categoryId, locale, site]);

  const prefetchedCategory = categoryQueryKey ? queryClient.getQueryData(categoryQueryKey) : undefined;

  const {data: categoryForPage, isFetched: categoryFetched} = useCategory(
    {parameters: {id: categoryId}},
    {enabled: !!categoryId}
  );

  const categoryForPageValidation = categoryForPage ?? prefetchedCategory;

  const {data: productForPage, error: productError, isFetched: productFetched} = useProduct(
    {
      parameters: {
        id: productId,
        perPricebook: true,
        expand: PRODUCT_SEO_EXPAND,
        allImages: false,
      },
    },
    {
      enabled: !!(page || (!pageShellFetched && cachedPage)) && !!productId,
    }
  );

  const productForSeo = useMemo(() => {
    if (!productId) return undefined;
    if (productForPage) return productForPage;

    const cachedProduct = queryClient.getQueryData(
      buildProductSeoQueryKey({locale, productId, site})
    );

    if (cachedProduct) return cachedProduct;

    const prefetched = res?.locals?.__seoProductPrefetch;
    const prefetchedId = prefetched?.id ?? prefetched?.productId;

    return prefetched && prefetchedId === productId ? prefetched : undefined;
  }, [productForPage, productId, locale, site, queryClient, res]);

  const extraPageMetaTags = useMemo(
    () => (productForSeo?.pageMetaTags ?? []).filter(({id}) => !SEO_MANAGED_META_NAMES.has(id)),
    [productForSeo?.pageMetaTags]
  );

  const {data: storesDataForPage, error: storeError, isFetched: storesFetched} = useStores(
    {parameters: {ids: storeId}},
    {enabled: !!page && !!storeId && !isCrossSiteStore}
  );

  const {
    data: adminStoreForPage,
    error: adminStoreError,
    isFetched: adminStoreFetched,
  } = useQuery({
    queryKey: adminStoreByIdQueryKey(storeSiteId, storeId),
    queryFn: () => fetchAdminStoreById({
      id: storeId,
      siteId: storeSiteId,
      countryCode: cmsStoreSiteId,
      getTokenWhenReady,
    }),
    enabled: !!page && !!storeId && isCrossSiteStore,
    staleTime: ADMIN_STORES_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const storeValidationFetched = isCrossSiteStore ? adminStoreFetched : storesFetched;

  const validationLoading = useMemo(() => {
    if (!page) return false;
    if (categoryId && !categoryFetched) return true;
    if (productId && !productFetched) return true;
    if (storeId && !storeValidationFetched) return true;

    return false;
  }, [page, categoryId, categoryFetched, productId, productFetched, storeId, storeValidationFetched]);

  const hasStoreError = isCrossSiteStore ?
    (!!storeId && adminStoreFetched && (adminStoreError || !adminStoreForPage)) :
    (!!storeId && storesFetched && (storeError || !storesDataForPage?.data?.length));

  const shouldShowNotFoundForPage = useMemo(() => {
    if (!page) return false;
    if (categoryId && categoryFetched && !categoryForPageValidation) return true;
    if (productId && productFetched && (productError || !productForPage) && !productForSeo) return true;
    if (hasStoreError) return true;

    return false;
  }, [page, categoryId, categoryForPageValidation, categoryFetched, productId, productError, productForPage, productForSeo, productFetched, hasStoreError]);

  const pageForSeo = pageShell ?? page ?? (!pageShellFetched ? cachedPage : null);

  const faqQuestions = useMemo(
    () => res?.locals?.__seoFaqQuestions,
    [res]
  );

  const blogForSeo = res?.locals?.__seoBlogPrefetch;

  const isSeoReady = useMemo(() => {
    if (tryCategoryFallback) return categoryFallbackFetched;
    if (!pageForSeo) return false;
    if (productId && !productForSeo && !productFetched) return false;
    if (categoryId && !categoryForPageValidation && !categoryFetched) return false;
    if (isFaqCmsPage(pageForSeo) && typeof window === 'undefined' && !faqQuestions?.length) {
      return false;
    }
    if (
      isBlogArticlePage(pageForSeo) &&
      findBlogIdInMainRegion(pageSlim ?? pageForSeo) &&
      typeof window === 'undefined' &&
      !blogForSeo
    ) {
      return false;
    }

    return true;
  }, [
    tryCategoryFallback,
    categoryFallbackFetched,
    pageForSeo,
    pageSlim,
    productId,
    productForSeo,
    productFetched,
    categoryId,
    categoryForPageValidation,
    categoryFetched,
    faqQuestions,
    blogForSeo
  ]);

  const pageSeoBundle = useMemo(
    () => (pageForSeo ?
      buildPageSeoBundle({
        appOrigin,
        blog: blogForSeo,
        category: categoryForPageValidation,
        currency,
        faqQuestions,
        page: pageForSeo,
        pathname,
        product: productForSeo,
        siteId: site?.id,
      }) :
      null),
    [pageForSeo, categoryForPageValidation, productForSeo, blogForSeo, faqQuestions, currency, appOrigin, pathname, site?.id]
  );

  const categoryFallbackSeoBundle = useMemo(
    () => (tryCategoryFallback ?
      buildPageSeoBundle({
        appOrigin,
        category: categoryFromPath || {id: categoryIdFromPath},
        currency,
        pathname,
        siteId: site?.id,
      }) :
      null),
    [
      tryCategoryFallback,
      categoryFromPath,
      categoryIdFromPath,
      appOrigin,
      pathname,
      currency,
      site?.id
    ]
  );

  // Apply page--404 to body when the page resolves to NotFound (no Contentful page + no category fallback,
  // or Contentful page found but SFCC validation failed).
  const isNotFound = useMemo(() => {
    if (isSystemPath) return false;
    if (page && shouldShowNotFoundForPage) return true;
    if (pageFetched && !pageShell && !pageSlim && !tryCategoryFallback) return true;
    if (tryCategoryFallback && categoryFallbackFetched && !categoryFromPath) return true;

    return false;
  }, [isSystemPath, page, pageShell, pageSlim, shouldShowNotFoundForPage, pageFetched, tryCategoryFallback, categoryFallbackFetched, categoryFromPath]);

  useBodyPageType(isNotFound ? '404' : null);

  if (isSystemPath) {
    return <PageNotFound />;
  }

  if (pageShellLoading && !pageForSeo) return mainLoadingFallback;
  if (page && shouldShowNotFoundForPage) return <PageNotFound />;

  if (pageForSeo) {
    const showMainLoading = validationLoading || !hasSlimMainBody;

    if (!productId && !isSeoReady) {
      return mainLoadingFallback;
    }

    return (
      <>
        {isSeoReady ? (
          <DynamicPageSeo seoBundle={pageSeoBundle}>
            {extraPageMetaTags.map(({id, value}) => (
              <meta key={id} content={value} name={id} />
            ))}
          </DynamicPageSeo>
        ) : null}
        {showMainLoading ? mainLoadingFallback : <ContentfulPage page={pageSlim} suppressSeo />}
      </>
    );
  }

  if (tryCategoryFallback) {
    if (!categoryFallbackFetched) return contentfulPageFallback;
    if (!categoryFromPath) return <PageNotFound />;

    return (
      <>
        <DynamicPageSeo seoBundle={categoryFallbackSeoBundle} />
        <ProductList categoryId={categoryIdFromPath} />
      </>
    );
  }

  if (pageFetched && !pageShell && !pageSlim) return <PageNotFound />;

  return mainLoadingFallback;
}

DynamicPage.displayName = 'DynamicPage';

export default DynamicPage;
